<template>
  <div class="app-shell">

    <!-- Global loading bar -->
    <transition name="sc-load">
      <div v-if="httpLoading" class="sc-progress" role="progressbar" aria-label="Loading">
        <div class="sc-progress__bar"></div>
      </div>
    </transition>

    <!-- ── Top nav ── -->
    <header class="app-nav" :class="{ 'app-nav--shadow': scrolled }">
      <div class="sc-container app-nav__inner">

        <!-- Logo -->
        <router-link to="/listing" class="app-nav__logo">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else>Structure<em>city</em></span>
        </router-link>

        <!-- Desktop nav links -->
        <nav class="app-nav__links">
          <router-link to="/listing" class="app-nav__link">Feed</router-link>
          <router-link to="/listings" class="app-nav__link">Browse</router-link>
          <router-link to="/messages" class="app-nav__link">Messages</router-link>
          <router-link to="/blog" class="app-nav__link">Blog</router-link>
        </nav>

        <!-- Search -->
        <div class="app-nav__search" :class="{ 'app-nav__search--open': searchOpen }" ref="searchWrap">
          <transition name="search-expand">
            <input
              v-if="searchOpen"
              ref="searchInput"
              class="app-nav__search-input"
              type="search"
              placeholder="Search projects, people, companies…"
              v-model="searchQuery"
              @keydown.enter="doSearch"
              @keydown.esc="closeSearch"
            />
          </transition>
          <button
            class="app-nav__search-btn"
            :class="{ 'app-nav__search-btn--active': searchOpen }"
            @click.stop="toggleSearch"
            aria-label="Search"
          >
            <svg v-if="!searchOpen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <template v-if="isLoggedIn">
        <!-- Notification bell -->
        <div class="notif-wrap" ref="notifBtn">
          <button class="notif-btn" @click.stop="toggleNotif" aria-label="Notifications">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span v-if="unreadCount > 0" class="notif-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
          </button>

          <transition name="app-drop">
            <div v-if="notifOpen" class="notif-drop" @click.stop>
              <div class="notif-drop__header">
                <span class="notif-drop__title">Notifications</span>
                <button v-if="unreadCount > 0" class="notif-drop__mark-all" @click="markAllRead">Mark all read</button>
              </div>
              <div class="notif-drop__body">
                <div v-if="notifLoading" class="notif-empty">Loading…</div>
                <div v-else-if="!notifications.length" class="notif-empty">No notifications yet</div>
                <button
                  v-else
                  v-for="n in notifications"
                  :key="n.id"
                  class="notif-item"
                  :class="{ 'notif-item--unread': n.status == 0 }"
                  @click="handleNotif(n)"
                >
                  <span v-if="n.status == 0" class="notif-item__dot"></span>
                  <span class="notif-item__text">{{ n.description }}</span>
                  <span class="notif-item__time">{{ timeAgo(n.created_at) }}</span>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <!-- User menu -->
        <div class="app-nav__right">
          <div class="app-nav__user" @click.stop="dropOpen = !dropOpen" ref="userBtn">
            <div class="app-nav__avatar">
              <img v-if="user && user.avatar" :src="$avatarSrc(user.avatar, 'users')" :alt="user.name" />
              <span v-else>{{ initial }}</span>
            </div>
            <svg class="app-nav__chevron" :class="{ 'app-nav__chevron--open': dropOpen }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

          <!-- Dropdown -->
          <transition name="app-drop">
            <div v-if="dropOpen" class="app-drop" @click.stop>
              <div class="app-drop__header">
                <strong>{{ user ? user.name : 'User' }}</strong>
                <span>{{ user ? user.email : '' }}</span>
              </div>
              <div class="app-drop__divider"></div>
              <router-link :to="'/sc/profile/' + slug" class="app-drop__item" @click="dropOpen = false">View Profile</router-link>
              <router-link to="/profile/update" class="app-drop__item" @click="dropOpen = false">Edit Profile</router-link>
              <router-link to="/profile/change-password" class="app-drop__item" @click="dropOpen = false">Change Password</router-link>
              <router-link to="/notification-settings" class="app-drop__item" @click="dropOpen = false">Notification Settings</router-link>
              <div class="app-drop__divider"></div>
              <router-link to="/my-followers" class="app-drop__item" @click="dropOpen = false">Following</router-link>
              <router-link to="/my-Projects" class="app-drop__item" @click="dropOpen = false">My Projects</router-link>
              <router-link to="/my-blog-history" class="app-drop__item" @click="dropOpen = false">My Blog</router-link>
              <router-link to="/my-service-requests" class="app-drop__item" @click="dropOpen = false">Service Requests</router-link>
              <div class="app-drop__divider"></div>
              <router-link to="/my-companies" class="app-drop__item" @click="dropOpen = false">My Companies</router-link>
              <router-link to="/register-company" class="app-drop__item" @click="dropOpen = false">Register a Company</router-link>
              <div class="app-drop__divider"></div>
              <button class="app-drop__item app-drop__item--danger" @click="logout">Sign out</button>
            </div>
          </transition>
        </div>
        </template>
        <template v-else>
          <router-link to="/sign-in" class="app-nav__login-btn">Login</router-link>
        </template>

        <!-- Hamburger -->
        <button class="app-nav__burger" @click="mobileOpen = !mobileOpen" aria-label="Toggle menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileOpen" class="app-nav__mobile">
        <div class="app-mobile__search">
          <input
            class="app-mobile__search-input"
            type="search"
            placeholder="Search…"
            v-model="searchQuery"
            @keydown.enter="doSearch"
          />
          <button class="app-mobile__search-btn" @click="doSearch" aria-label="Search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </button>
        </div>
        <router-link to="/listing" class="app-mobile__link" @click="mobileOpen = false">Feed</router-link>
        <router-link to="/listings" class="app-mobile__link" @click="mobileOpen = false">Browse</router-link>
        <router-link to="/messages" class="app-mobile__link" @click="mobileOpen = false">Messages</router-link>
        <router-link to="/blog" class="app-mobile__link" @click="mobileOpen = false">Blog</router-link>
        <template v-if="isLoggedIn">
          <button class="app-mobile__link app-mobile__link--notif" @click="mobileOpen = false; notifOpen = true; fetchNotifications()">
            Notifications
            <span v-if="unreadCount > 0" class="app-mobile__notif-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
          </button>
          <router-link :to="'/sc/profile/' + slug" class="app-mobile__link" @click="mobileOpen = false">My Profile</router-link>
          <router-link to="/profile/update" class="app-mobile__link" @click="mobileOpen = false">Settings</router-link>
          <div class="app-drop__divider" style="margin:.25rem 0"></div>
          <router-link to="/my-companies" class="app-mobile__link" @click="mobileOpen = false">My Companies</router-link>
          <router-link to="/register-company" class="app-mobile__link" @click="mobileOpen = false">Register a Company</router-link>
          <div class="app-drop__divider" style="margin:.25rem 0"></div>
          <button class="app-mobile__link app-mobile__link--danger" @click="logout">Sign out</button>
        </template>
        <template v-else>
          <div class="app-drop__divider" style="margin:.25rem 0"></div>
          <router-link to="/sign-in" class="app-mobile__login-btn" @click="mobileOpen = false">Login</router-link>
        </template>
      </div>
    </header>

    <!-- ── Page content ── -->
    <main class="app-main">
      <slot />
    </main>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AppLayout',

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: null,
      user: null,
      myCompanies: [],
      httpLoading: false,
      dropOpen: false,
      mobileOpen: false,
      searchOpen: false,
      searchQuery: '',
      scrolled: false,
      slug: localStorage.getItem('slug') || '',
      isLoggedIn: !!localStorage.getItem('token'),
      notifOpen: false,
      notifications: [],
      unreadCount: 0,
      notifLoading: false,
    };
  },

  computed: {
    initial() {
      return this.user && this.user.fname ? this.user.fname[0].toUpperCase() : 'U';
    },
  },

  mounted() {
    this.fetchCompany();
    this.fetchUser();
    this.fetchUserCompanies();
    this.fetchUnreadCount();
    this._notifPoll = setInterval(this.fetchUnreadCount, 60000);
    this._onHttpLoading = (v) => { this.httpLoading = v; };
    window.Fire.on('sc:loading', this._onHttpLoading);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('click', this.onDocClick);
  },

  beforeUnmount() {
    clearInterval(this._notifPoll);
    window.Fire.off('sc:loading', this._onHttpLoading);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocClick);
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.push({ name: 'Login' });
    },

    onScroll() {
      this.scrolled = window.scrollY > 4;
    },

    onDocClick(e) {
      if (this.$refs.userBtn && !this.$refs.userBtn.contains(e.target)) {
        this.dropOpen = false;
      }
      if (this.$refs.searchWrap && !this.$refs.searchWrap.contains(e.target)) {
        this.closeSearch();
      }
      if (this.$refs.notifBtn && !this.$refs.notifBtn.contains(e.target)) {
        this.notifOpen = false;
      }
    },

    toggleSearch() {
      if (this.searchOpen) {
        this.doSearch();
      } else {
        this.searchOpen = true;
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },

    closeSearch() {
      this.searchOpen = false;
      this.searchQuery = '';
    },

    doSearch() {
      const q = this.searchQuery.trim();
      if (!q) { this.closeSearch(); return; }
      this.$router.push({ path: '/search', query: { q } });
      this.closeSearch();
      this.mobileOpen = false;
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res && res.status === 200) this.company = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      const userId = localStorage.getItem('user');
      if (!userId) return;
      axios(process.env.VUE_APP_API_URL + 'user/details/' + userId)
        .then(res => { if (res && res.status === 200) this.user = res.data; })
        .catch(() => {});
    },

    fetchUserCompanies() {
      const userId = localStorage.getItem('user');
      if (!userId) return;
      axios(process.env.VUE_APP_API_URL + 'user/companies/' + userId)
        .then(res => { if (res.data && res.data.data) this.myCompanies = res.data.data; })
        .catch(() => {});
    },

    fetchUnreadCount() {
      const userId = localStorage.getItem('user');
      if (!userId) return;
      axios(process.env.VUE_APP_API_URL + 'user/unread-notification/' + userId)
        .then(res => { if (res && res.status === 200) this.unreadCount = res.data || 0; })
        .catch(() => {});
    },

    fetchNotifications() {
      const userId = localStorage.getItem('user');
      if (!userId) return;
      this.notifLoading = true;
      axios(process.env.VUE_APP_API_URL + 'user/notifications/' + userId)
        .then(res => {
          if (res && res.status === 200) this.notifications = res.data || [];
        })
        .catch(() => {})
        .finally(() => { this.notifLoading = false; });
    },

    toggleNotif() {
      this.notifOpen = !this.notifOpen;
      if (this.notifOpen) this.fetchNotifications();
    },

    handleNotif(n) {
      if (n.status == 0) {
        n.status = 1;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        axios(process.env.VUE_APP_API_URL + 'user/update-notifications/' + n.id).catch(() => {});
      }
      this.notifOpen = false;
      if (n.link) this.$router.push(n.link).catch(() => {});
    },

    markAllRead() {
      const unread = this.notifications.filter(n => n.status == 0);
      unread.forEach(n => {
        n.status = 1;
        axios(process.env.VUE_APP_API_URL + 'user/update-notifications/' + n.id).catch(() => {});
      });
      this.unreadCount = 0;
    },

    timeAgo(dateStr) {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    },
  },
};
</script>

<style scoped>
/* ── Global loading bar ── */
.sc-progress {
  position: fixed; top: 0; left: 0; right: 0;
  height: 3px; z-index: 9999; overflow: hidden;
  pointer-events: none;
  background: rgba(30, 58, 95, .12);
}
.sc-progress__bar {
  height: 100%;
  background: var(--primary, #1e3a5f);
  animation: sc-indeterminate 1.6s cubic-bezier(.4, 0, .2, 1) infinite;
}
@keyframes sc-indeterminate {
  0%   { transform: translateX(-100%) scaleX(.5); }
  40%  { transform: translateX(0%)    scaleX(.4); }
  100% { transform: translateX(100%) scaleX(.5); }
}
.sc-load-enter-active, .sc-load-leave-active { transition: opacity .15s; }
.sc-load-enter-from, .sc-load-leave-to { opacity: 0; }

/* ── Shell ── */
.app-shell { min-height: 100vh; background: var(--surface-2, #f8fafc); }

/* ── Nav ── */
.app-nav {
  position: sticky; top: 0; z-index: 200;
  background: #fff;
  border-bottom: 1px solid var(--border, #e5e7eb);
  transition: box-shadow .2s;
}
.app-nav--shadow { box-shadow: 0 2px 12px rgba(0,0,0,.07); }

.app-nav__inner {
  display: flex; align-items: center; gap: 1.25rem;
  height: 64px;
}

/* Logo */
.app-nav__logo {
  display: flex; align-items: center; gap: .5rem;
  text-decoration: none; flex-shrink: 0;
  font-size: 1.3rem; font-weight: 800;
  color: var(--primary, #1e3a5f); letter-spacing: -.03em;
}
.app-nav__logo img { height: 34px; object-fit: contain; }
.app-nav__logo em { color: var(--accent, #f59e0b); font-style: normal; }
.app-nav__logo:hover { text-decoration: none; }

/* Desktop nav */
.app-nav__links { display: flex; align-items: center; gap: .125rem; flex: 1; }
.app-nav__link {
  padding: .375rem .875rem; border-radius: 99px;
  font-size: .9rem; font-weight: 500;
  color: var(--text-muted, #6b7280); text-decoration: none;
  transition: background .15s, color .15s;
}
.app-nav__link:hover,
.app-nav__link.router-link-active {
  background: var(--surface-3, #f1f5f9);
  color: var(--primary, #1e3a5f); font-weight: 600;
  text-decoration: none;
}

/* Search */
.app-nav__search {
  display: flex; align-items: center; gap: .25rem;
  position: relative;
}
.app-nav__search-input {
  width: 0;
  padding: .375rem .75rem;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 99px;
  font-size: .875rem;
  outline: none;
  background: var(--surface-2, #f8fafc);
  color: var(--text, #111827);
  font-family: inherit;
  transition: width .25s ease, border-color .15s;
  width: 220px;
}
.app-nav__search-input:focus { border-color: var(--primary, #1e3a5f); background: #fff; }
.app-nav__search-input::placeholder { color: var(--text-muted, #9ca3af); }
.app-nav__search-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: none; background: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s;
  flex-shrink: 0;
}
.app-nav__search-btn svg { pointer-events: none; }
.app-nav__search-btn:hover,
.app-nav__search-btn--active { background: var(--surface-3, #f1f5f9); color: var(--primary, #1e3a5f); }

/* Search expand animation */
.search-expand-enter-active, .search-expand-leave-active { transition: width .25s ease, opacity .2s; }
.search-expand-enter-from, .search-expand-leave-to { width: 0; opacity: 0; }

/* Right side */
.app-nav__right {
  position: relative; flex-shrink: 0;
  display: flex; align-items: center;
}

.app-nav__user {
  display: flex; align-items: center; gap: .375rem;
  cursor: pointer; padding: .25rem .5rem .25rem .25rem;
  border-radius: 99px; transition: background .15s;
}
.app-nav__user:hover { background: var(--surface-3, #f1f5f9); }

.app-nav__avatar {
  width: 34px; height: 34px; border-radius: 50%;
  overflow: hidden; background: var(--primary, #1e3a5f);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: .8125rem; flex-shrink: 0;
}
.app-nav__avatar img { width: 100%; height: 100%; object-fit: cover; }

.app-nav__chevron { color: var(--text-muted, #6b7280); transition: transform .2s; }
.app-nav__chevron--open { transform: rotate(180deg); }

/* Dropdown */
.app-drop {
  position: absolute; top: calc(100% + .5rem); right: 0;
  background: #fff; border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-lg, 1.25rem);
  box-shadow: 0 20px 48px rgba(0,0,0,.12), 0 8px 16px rgba(0,0,0,.06);
  min-width: 220px; overflow: hidden;
}

.app-drop__header { padding: .875rem 1rem; }
.app-drop__header strong { display: block; font-size: .9375rem; font-weight: 700; color: var(--text, #111827); }
.app-drop__header span { font-size: .8125rem; color: var(--text-muted, #6b7280); }

.app-drop__divider { height: 1px; background: var(--border-light, #f3f4f6); margin: .25rem 0; }

.app-drop__item {
  display: block; width: 100%; padding: .625rem 1rem;
  font-size: .875rem; font-weight: 500; color: var(--text, #111827);
  text-decoration: none; text-align: left; cursor: pointer;
  background: none; border: none; font-family: inherit;
  transition: background .12s;
}
.app-drop__item:hover { background: var(--surface-2, #f8fafc); color: var(--primary, #1e3a5f); text-decoration: none; }
.app-drop__item--danger { color: var(--danger, #ef4444); }
.app-drop__item--danger:hover { background: #fef2f2; }
.app-drop__item--muted { color: var(--text-muted, #6b7280); font-size: .8125rem; }
.app-drop__section {
  display: block; padding: .375rem 1rem .125rem;
  font-size: .6875rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: var(--text-muted, #9ca3af);
}

/* Dropdown animation */
.app-drop-enter-active, .app-drop-leave-active { transition: opacity .15s, transform .15s; }
.app-drop-enter-from, .app-drop-leave-to { opacity: 0; transform: translateY(-6px); }

/* Guest login button – desktop */
.app-nav__login-btn {
  display: inline-flex; align-items: center; padding: .4rem 1.1rem;
  background: var(--primary, #1e3a5f); color: #fff;
  border-radius: 6px; font-size: .875rem; font-weight: 600;
  text-decoration: none; transition: background .15s;
}
.app-nav__login-btn:hover { background: #162d4a; color: #fff; text-decoration: none; }

/* Burger */
.app-nav__burger {
  display: none; background: none; border: none;
  cursor: pointer; color: var(--text, #111827);
  padding: .375rem; margin-left: auto; border-radius: .5rem;
  transition: background .15s;
}
.app-nav__burger:hover { background: var(--surface-3, #f1f5f9); }

/* Mobile search */
.app-mobile__search {
  display: flex; align-items: center; gap: .5rem;
  padding: .75rem 1.25rem;
  border-bottom: 1px solid var(--border, #e5e7eb);
}
.app-mobile__search-input {
  flex: 1; padding: .5rem .875rem;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 99px; font-size: .875rem;
  outline: none; background: var(--surface-2, #f8fafc);
  color: var(--text, #111827); font-family: inherit;
}
.app-mobile__search-input:focus { border-color: var(--primary, #1e3a5f); }
.app-mobile__search-btn {
  width: 34px; height: 34px; border-radius: 50%;
  border: none; background: var(--primary, #1e3a5f);
  color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Mobile menu */
.app-nav__mobile {
  border-top: 1px solid var(--border, #e5e7eb);
  padding: .5rem 0; background: #fff;
}
.app-mobile__link {
  display: block; padding: .75rem 1.5rem;
  font-size: .9375rem; font-weight: 500; color: var(--text, #111827);
  text-decoration: none; transition: background .12s;
  background: none; border: none; font-family: inherit;
  width: 100%; text-align: left; cursor: pointer;
}
.app-mobile__link:hover { background: var(--surface-2, #f8fafc); text-decoration: none; }
.app-mobile__link--danger { color: var(--danger, #ef4444); }
.app-mobile__link--notif { display: flex; align-items: center; gap: .5rem; }
/* Guest login button – mobile */
.app-mobile__login-btn {
  display: block; margin: .5rem 1.5rem; padding: .6rem 1.25rem;
  background: var(--primary, #1e3a5f); color: #fff;
  border-radius: 6px; font-size: .9375rem; font-weight: 600;
  text-align: center; text-decoration: none; transition: background .15s;
}
.app-mobile__login-btn:hover { background: #162d4a; color: #fff; text-decoration: none; }
.app-mobile__notif-badge {
  min-width: 18px; height: 18px; padding: 0 4px;
  background: var(--danger, #ef4444); color: #fff;
  border-radius: 99px; font-size: .6875rem; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}

/* Notification bell */
.notif-wrap {
  position: relative; flex-shrink: 0;
}
.notif-btn {
  position: relative;
  width: 36px; height: 36px; border-radius: 50%;
  border: none; background: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s;
}
.notif-btn:hover { background: var(--surface-3, #f1f5f9); color: var(--primary, #1e3a5f); }
.notif-badge {
  position: absolute; top: 3px; right: 3px;
  min-width: 16px; height: 16px; padding: 0 3px;
  background: var(--danger, #ef4444); color: #fff;
  border-radius: 99px; font-size: .625rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  line-height: 1; border: 2px solid #fff;
}
.notif-drop {
  position: absolute; top: calc(100% + .5rem); right: -8px;
  background: #fff; border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-lg, 1.25rem);
  box-shadow: 0 20px 48px rgba(0,0,0,.12), 0 8px 16px rgba(0,0,0,.06);
  width: 320px; overflow: hidden; z-index: 300;
}
.notif-drop__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: .75rem 1rem .625rem;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
}
.notif-drop__title { font-size: .9375rem; font-weight: 700; color: var(--text, #111827); }
.notif-drop__mark-all {
  font-size: .75rem; font-weight: 600; color: var(--primary, #1e3a5f);
  background: none; border: none; cursor: pointer; padding: .25rem .5rem;
  border-radius: .375rem; transition: background .12s;
}
.notif-drop__mark-all:hover { background: var(--surface-2, #f8fafc); }
.notif-drop__body { max-height: 360px; overflow-y: auto; }
.notif-empty {
  padding: 2rem 1rem; text-align: center;
  font-size: .875rem; color: var(--text-muted, #9ca3af);
}
.notif-item {
  display: flex; flex-direction: column; gap: .25rem;
  width: 100%; padding: .75rem 1rem;
  text-align: left; background: none; border: none;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
  cursor: pointer; font-family: inherit;
  position: relative; transition: background .12s;
}
.notif-item:last-child { border-bottom: none; }
.notif-item:hover { background: var(--surface-2, #f8fafc); }
.notif-item--unread { background: #eff6ff; }
.notif-item--unread:hover { background: #dbeafe; }
.notif-item__dot {
  position: absolute; top: 50%; right: 1rem; transform: translateY(-50%);
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--primary, #1e3a5f);
}
.notif-item__text {
  font-size: .875rem; color: var(--text, #111827);
  font-weight: 500; padding-right: 1.25rem; line-height: 1.4;
}
.notif-item__time { font-size: .75rem; color: var(--text-muted, #9ca3af); }

/* Main */
.app-main { min-height: calc(100vh - 64px); }

/* Responsive */
@media (max-width: 768px) {
  .app-nav__links { display: none; }
  .app-nav__burger { display: flex; }
  .app-nav__chevron { display: none; }
  .app-nav__search-input { width: min(170px, calc(100vw - 200px)); }

  .notif-wrap { position: static; }
  .notif-drop {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: auto;
    border-radius: 0 0 var(--radius-lg, 1.25rem) var(--radius-lg, 1.25rem);
  }
  .notif-drop__body { max-height: calc(100vh - 130px); }
}
</style>
